import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject, input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import {
  FwCheckboxModule,
  FwDialogConfirmComponent,
  FwDialogService,
  FwDialogsModule,
  FwIconButtonModule,
  FwIconModule,
  FwMenuModule,
  FwSelectMenuModule,
  FwTextInputModule,
  FwTooltipModule,
  FwTypeaheadComponent,
  FwWrappedInputModule,
} from '@flywheel-io/vision';
import isEqual from 'lodash/isEqual';
import { delay } from 'rxjs/operators';

import { GearConfig, GearConfigInvocation } from '../../shared/models/gear.model';
import { replayValue } from '../../shared/observable.utils';
import { coerceConfig, getConfigArrayValidators, getConfigValidators, isConfigArray } from '../gear.utils';

@Component({
  host: {
    'test-id': 'gear-config',
  },
  imports: [
    CommonModule,
    FwCheckboxModule,
    FwDialogsModule,
    FwIconButtonModule,
    FwIconModule,
    FwMenuModule,
    FwSelectMenuModule,
    FwTextInputModule,
    FwTooltipModule,
    FwTypeaheadComponent,
    FwWrappedInputModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => GearConfigComponent),
      multi: true,
    },
  ],
  selector: 'gear-config',
  styleUrls: ['./gear-config.component.scss'],
  templateUrl: './gear-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GearConfigComponent implements ControlValueAccessor, OnChanges {
  config = input<GearConfig>({})

  private dialog = inject(FwDialogService);

  form = new FormGroup({})

  /* eslint-disable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */
  private onChange = (value: GearConfigInvocation): void => {}
  private onTouched = (): void => {}
  /* eslint-enable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      const prevConfig: GearConfig = changes.config.previousValue;
      const prevValues: GearConfigInvocation = this.form.value;
      const wasDisabled = this.form.disabled;
      this.form = new FormGroup(Object.assign({},
        ...Object.entries(this.config() ?? {})
          .map(([key, config]) => {
            let _default = config.default;
            if (config.type === 'boolean' && typeof _default !== 'boolean') {
              _default = false;
            }
            // restore values that were not backed by a config, or backed by the same config
            if (key in prevValues && (!(key in prevConfig) || isEqual(config, prevConfig[key]))) {
              _default = prevValues[key];
            }
            if (isConfigArray(config)) {
              return { [key]: new FormControl(_default || [], getConfigArrayValidators(config)) };
            }
            return { [key]: new FormControl(_default, getConfigValidators(config)) };
          }),
      ));
      this.form.markAllAsTouched(); // light up required
      if (wasDisabled) {
        this.form.disable();
      }
      replayValue(this.form).pipe(
        delay(0), // allow emit during writeValue
      ).subscribe(values => {
        this.onChange(coerceConfig(values, this.config()));
      });
    }
  }

  get hasErrors(): boolean {
    return this.form.invalid;
  }

  toString(value: unknown): string {
    return String(value);
  }

  getPlaceholder(key: string): string {
    const configParameter = this.config()[key];
    const hasDefault = 'default' in configParameter;
    if (hasDefault && configParameter.default !== '') {
      return `Default: ${configParameter.default}`;
    }
    return '';
  }

  getError(key: string): string {
    const { errors, value } = this.form.get(key);
    if (!errors) {
      return '';
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-const
    let [validatorKey, validatorValue]: [string, any] = Object.entries(errors.arrayItems ?? errors)[0];
    if (Array.isArray(value) && !errors.arrayItems) {
      validatorKey = {
        'minlength': 'minItems',
        'maxlength': 'maxItems',
      }[validatorKey] ?? validatorKey;
    }

    const validationMessages: Record<string, string> = {
      'required': 'This value is required.',
      'minlength': `This value must be at least ${validatorValue?.requiredLength} characters in length.`,
      'maxlength': `This value must not exceed ${validatorValue?.requiredLength} characters in length.`,
      'pattern': `This value must match the pattern /${validatorValue?.requiredPattern}/.`,
      'typeNumber': 'This value must be a number.',
      'numberInteger': 'This value must be an integer.',
      'min': `This value must be greater than or equal to ${validatorValue?.min}.`,
      'max': `This value must be less than or equal to ${validatorValue?.max}.`,
      'minItems': `This array must contain at least ${validatorValue?.requiredLength} items.`,
      'maxItems': `This array must contain at most ${validatorValue?.requiredLength} items.`,
    };

    const message = validationMessages[validatorKey] || validatorKey;

    if (errors.arrayItems) {
      const [position, value] = validatorValue;
      return `${value || position}: ${message}`;
    }
    return message;
  }

  getDefaults(config: GearConfig = this.config()): GearConfigInvocation {
    return Object.assign({},
      ...Object.entries(config ?? {})
        .map(([key, config]) => {
          let _default = config.default;
          if (config.type === 'boolean' && typeof _default !== 'boolean') {
            _default = false;
          }
          if (config.type === 'array' && !Array.isArray(_default)) {
            _default = [];
          }
          return { [key]: _default };
        }),
    );
  }

  resetToDefaults(): void {
    const values = this.getDefaults();
    for (const key of Object.keys(values)) {
      if (typeof values[key] === 'undefined') {
        values[key] = null; // setValue / reset does not accept undefined
      }
    }
    this.form.setValue(values);
  }

  confirmReset(): void {
    const dialog = this.dialog.open(FwDialogConfirmComponent);
    dialog.componentInstance.title = 'Confirm Reset';
    dialog.componentInstance.contentText = 'Are you sure you want to reset to default configuration values?';
    dialog.componentInstance.confirm.subscribe(() => {
      this.resetToDefaults();
    });
  }

  /** ControlValueAccessor **/
  writeValue(value: GearConfigInvocation): void {
    for (const key of Object.keys(value ?? {})) {
      // if we receive values before config, store them in a new control
      if (!this.form.contains(key)) {
        this.form.addControl(key, new FormControl());
      }
    }
    this.form.patchValue(value);
  }

  registerOnChange(fn: (value: GearConfigInvocation) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(toDisabled: boolean): void {
    this.form[toDisabled ? 'disable' : 'enable']();
  }
}
